import { BalanceData, ChestBand, PlayerProfile, ReadinessTargetData, UpgradeTarget, averageChest, earlyResourceMult, eventAt, eventEndsAt, eventsUnlocked, ownedTowers, researchTowerLevels, vipTowerLevels } from "@highmarch/domain";
import { levelSeats, planLoadout } from "./gearPlan";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Am I ready for level n? (DESIGN §3.4, the hub's readiness strip.) Two meters against what the level was tuned for —
 * the gear score equipped where this level wants it against the precomputed target, and the roster's persistent level
 * against parity — read through bands calibrated on the strategic player (balance/src/readiness.json):
 * - `noGearNeeded`: levels before `gearRequiredFrom` (the towers meter still reads).
 * - `ready`: gear ≥ readyGearPercent of its target, and towers ≥ readyTowerPercent of parity and no more than
 *   readyTowerLevelsUnder levels under it (calibration in readiness.json).
 * - `underGeared`: even "Equip best" leaves gear under underGearPercent, or towers are under underTowerPercent or more
 *   than underTowerLevelsUnder levels under parity. Equipping alone cannot fix it; the actions say what can.
 * - `equip`: in between (calibration: 60 %). Usually there is gear to put on, from the bag or a benched tower
 *   (`equippable` > 0, the EQUIP n button); when there is none, the actions (upgrade, replay) are the way to ready.
 * VIP (docs/SHOP.md §8.6): targets and bands stay calibrated at VIP 0; the player's VIP tower damage counts on the towers
 * meter as the tower levels it equals (`vipTowerLevels`: +16 % = 5.0), so Ready keeps meaning a ~70 % win for this
 * player. Only tower damage counts (fortress health and troops are extra margin); the gear meter never sees VIP.
 * Gold Research (docs/RESEARCH.md) counts the same way: each owned node adds the tower levels it is worth (research.json
 * `worth`) to its tower (or the Fortress) on the towers meter — the balance report's reference player carries the
 * expected research with its towers lowered by that worth, so the targets keep their meaning.
 * Pure and cheap (a seat plan over the inventory; no sim), so the hub can call it on every render.
 */
export type ReadinessState = "noGearNeeded" | "ready" | "equip" | "underGeared";

export interface ReadinessAction {
  readonly kind: "equip" | "upgradeTower" | "replay";
  readonly label: string;
  /** upgradeTower: the tower id ("base" for the fortress). */
  readonly towerId?: string;
  /** upgradeTower: the level to upgrade it to; replay: the level to replay. */
  readonly level?: number;
}

export interface Meter { readonly have: number; readonly target: number }

export interface Readiness {
  readonly state: ReadinessState;
  /** Gear is part of the plan from here on (level ≥ gearRequiredFrom). */
  readonly gearRequired: boolean;
  /** This is the first level that needs gear (the "GEAR REQUIRED FROM HERE" kicker). */
  readonly gearRequiredFromHere: boolean;
  /** Gear score equipped where this level wants it vs the level's target. */
  readonly gear: Meter;
  /** Mean persistent level of the base and the owned tower types plus `vipLevels` (one decimal) vs parity. */
  readonly towers: Meter;
  /** The tower levels the player's VIP tower damage is worth, already inside `towers.have` (the strip's "+n.n VIP"; 0 at VIP 0). */
  readonly vipLevels: number;
  /** The mean tower levels the fielded towers' Gold Research is worth, already inside `towers.have` (0 with none). */
  readonly researchLevels: number;
  /** Items "Equip best" for this level would put onto its seats, from the bag or a tower it does not field (the EQUIP n
   *  count and the Gear badge; `equipCount` with the same scope). */
  readonly equippable: number;
  /** Gear score after "Equip best" for this level. */
  readonly gearAfterEquip: number;
  /** What keeps the level from `ready`. */
  readonly shortfall: "none" | "gear" | "towers" | "both";
  /** Up to MAX_ACTIONS ways to get stronger, most useful first. */
  readonly actions: readonly ReadinessAction[];
}

export interface ReadinessOptions {
  /** Tower types owned; defaults to those owned when this level is next (unlocks up to it). */
  readonly roster?: readonly boolean[];
  /** Highest level cleared, for the replay suggestion; defaults to level − 1. */
  readonly highestCleared?: number;
}

/** The "Get stronger" list holds at most this many actions (Lobby plan: ≤ 3 ranked). */
export const MAX_ACTIONS = 3;
/** Tower meter precision: one decimal, so "4.5 of 5" reads as the half step it is. */
const TOWER_METER_STEPS = 10;
const PERCENT = 100;
const NO_TARGET: ReadinessTargetData = { gear: 0, towers: 1, seats: [] };

export function readinessFor(profile: PlayerProfile, level: number, opts: ReadinessOptions = {}): Readiness {
  const B = profile.balance;
  const R = B.readiness;
  const target = R.targets[level - 1] ?? NO_TARGET;
  const roster = opts.roster ?? ownedTowers(B, level - 1);
  const gearRequired = level >= R.gearRequiredFrom;

  const plan = planLoadout(B, profile.gear, levelSeats(profile, level, roster));
  const gear = { have: plan.scoreBefore, target: target.gear };
  const fielded = [B.baseTowerIndex, ...B.towers.map((_, i) => i).filter((i) => roster[i] && !B.towers[i].isBase)];
  const levelOf = (i: number) => (i === B.baseTowerIndex ? profile.baseLevel : profile.towerLevels[i]);
  const vipLevels = vipTowerLevels(B, profile.vip.towerDamagePercent);
  const researchLevels = fielded.reduce((s, i) => s + researchTowerLevels(B, profile.research, i), 0) / fielded.length;
  const meanLevel = fielded.reduce((s, i) => s + levelOf(i), 0) / fielded.length + researchLevels + vipLevels;
  const towers = { have: Math.round(meanLevel * TOWER_METER_STEPS) / TOWER_METER_STEPS, target: target.towers };

  // Before gear is required, gear never holds a level back.
  const gearAt = (score: number, percent: number) => !gearRequired || score * PERCENT >= percent * target.gear;
  // Towers: a share of parity and, late, at most so many levels under it (a percentage alone lets a level-40 player
  // sit 10 levels under parity and still read Ready; balance/src/readiness.json).
  const towersAt = (percent: number, levelsUnder: number) => meanLevel * PERCENT >= percent * target.towers && meanLevel >= target.towers - levelsUnder;
  const gearShort = !gearAt(gear.have, R.readyGearPercent);
  const gearShortAfterEquip = !gearAt(plan.scoreAfter, R.readyGearPercent);
  const towersShort = !towersAt(R.readyTowerPercent, R.readyTowerLevelsUnder);
  const shortfall = gearShort && towersShort ? "both" : gearShort ? "gear" : towersShort ? "towers" : "none";
  const equippable = plan.equips.length;

  let state: ReadinessState;
  if (!gearRequired) state = "noGearNeeded";
  else if (shortfall === "none") state = "ready";
  else if (!gearAt(plan.scoreAfter, R.underGearPercent) || !towersAt(R.underTowerPercent, R.underTowerLevelsUnder)) state = "underGeared";
  else state = "equip";

  // Get stronger: what closes the gap first. Equipping is instant, a tower level takes a builder, a replay a battle.
  const actions: ReadinessAction[] = [];
  if (equippable > 0) actions.push({ kind: "equip", label: `Equip ${equippable} item${equippable === 1 ? "" : "s"}: gear ${gear.have} → ${plan.scoreAfter}` });
  if (meanLevel < target.towers) {
    const pick = weakestFielded(profile, fielded, levelOf, target.seats);
    if (pick >= 0) {
      const t = B.towers[pick];
      actions.push({ kind: "upgradeTower", label: `Upgrade ${t.name} to level ${levelOf(pick) + 1}`, towerId: t.id, level: levelOf(pick) + 1 });
    }
  }
  const replay = Math.min(level - 1, opts.highestCleared ?? level - 1);
  if (replay >= 1 && (gearShortAfterEquip || towersShort)) {
    actions.push({ kind: "replay", label: `Farm level ${replay} for ${gearShortAfterEquip ? "gear chests" : "gold"}`, level: replay });
  }
  return { state, gearRequired, gearRequiredFromHere: level === R.gearRequiredFrom, gear, towers, vipLevels, researchLevels: Math.round(researchLevels * TOWER_METER_STEPS) / TOWER_METER_STEPS, equippable, gearAfterEquip: plan.scoreAfter, shortfall, actions: actions.slice(0, MAX_ACTIONS) };
}

/**
 * The words for a readiness state, the one source for every surface that names it (the hub's battle card, the Gear
 * screen). The amber band can come with nothing left to equip (gear fine, towers behind — or the bag already empty),
 * so the title follows what is actually short rather than always asking for gear. `gearFrom`: the first level that
 * needs gear, for the noGearNeeded wording (`null`/absent when none does).
 */
export function readinessTitle(r: Readiness, gearFrom: number | null = null): string {
  const towersOnly = r.shortfall === "towers";
  switch (r.state) {
    case "noGearNeeded": return gearFrom !== null ? `No gear needed until level ${gearFrom}` : "No gear needed";
    case "ready": return "Ready";
    case "underGeared": return towersOnly ? "Towers too weak" : "Under-geared";
    case "equip": return r.equippable > 0 ? "Equip your gear" : towersOnly ? "Almost ready" : "Gear falls short";
  }
}

/**
 * The fielded tower (or the base) most worth a level: the lowest, not already upgrading or at max; among equals, the
 * type this level's gear goes on most (it fights most), then the base, then roster order. −1 when none can go up.
 */
function weakestFielded(profile: PlayerProfile, fielded: readonly number[], levelOf: (i: number) => number, seats: readonly (readonly number[])[]): number {
  const B = profile.balance;
  const carried = (i: number) => seats.reduce((n, s) => n + (s.includes(i) ? 1 : 0), 0);
  const busy = (i: number) => (i === B.baseTowerIndex ? profile.isUpgrading(UpgradeTarget.Base, 0) : profile.isUpgrading(UpgradeTarget.Tower, i));
  let best = -1;
  for (const i of fielded) {
    if (busy(i) || levelOf(i) >= B.tables.maxLevel) continue;
    if (best < 0 || levelOf(i) < levelOf(best) || (levelOf(i) === levelOf(best) && carried(i) > carried(best))) best = i;
  }
  return best;
}

/**
 * The free wall's way through (docs/SHOP.md §3.3 K6): when the next level is not Ready, the battle card offers FARM
 * L{n} beside PLAY — a replay of the highest cleared level, whose farm chest pays more gold and more gear than a
 * plain win (chests.json farm bands) — and, while an event runs, points at it (its track pays supplies, speed-ups and
 * gear chests for the same battles). Every string the battle card, the readiness strip and the Armoury show for a
 * stuck player comes from here. Never mentions the shop: the quiet rules (shop.json _quiet) keep offers out of play.
 */
export interface FarmSuggestion {
  /** The level to replay (the highest cleared). */
  readonly level: number;
  /** The level being geared up for (the next one). */
  readonly forLevel: number;
  /** The button: "FARM L12". */
  readonly button: string;
  /** The payout line under it: "Farm chest: ~140 gold + 1 gear item, 35% chance of one more". */
  readonly payout: string;
  /** One sentence for the strip / Armoury: what is short and the two ways to fix it. */
  readonly hint: string;
  /** The event running now (the hint names it), or null. */
  readonly event: { readonly id: string; readonly name: string; readonly endsAt: number } | null;
  /** Expected gold of a farm chest at `level`. */
  readonly gold: number;
  /** Chance (percent) of the farm chest's extra gear item. */
  readonly extraGearPercent: number;
}

/** Rounds the resource estimate to a friendly figure: two significant digits (140, not 137). */
function roughly(n: number): number {
  const ROUND_TO = 10;
  if (n < ROUND_TO * ROUND_TO) return Math.max(1, Math.round(n));
  const p = 10 ** (Math.floor(Math.log10(n)) - 1);
  return Math.round(n / p) * p;
}

/** What a farm run (a win of a cleared level) pays at `level`: the rough gold, the extra-gear chance and the line. */
export interface FarmPayout { readonly gold: number; readonly extraGearPercent: number; readonly line: string }

/** The farm chest at `level` (chests.json farm bands): "Farm chest: ~140 gold + 1 gear item, 35% chance of one more". */
export function farmPayout(B: BalanceData, level: number): FarmPayout {
  const ch = B.chests;
  const gold = roughly(averageChest(B, level, ChestBand.Farm).mul(earlyResourceMult(B, level)).mul(ch.farmResourceMult).toNumber());
  const extra = ch.farmExtraGearPercent;
  return { gold, extraGearPercent: extra, line: `Farm chest: ~${gold} gold + 1 gear item${extra > 0 ? `, ${extra}% chance of one more` : ""}` };
}

/**
 * K6: the FARM suggestion for the next level, or null when there is nothing to farm for (Ready, before gear is
 * needed, nothing cleared yet, or the campaign done). `now` (Unix seconds) names a running event in the hint; null
 * leaves events out.
 */
export function farmSuggestion(catalog: LevelCatalog, now: number | null = null, r?: Readiness): FarmSuggestion | null {
  const B = catalog.balance, highest = catalog.highestLevelCleared;
  if (highest < 1 || highest >= catalog.levelCount) return null;
  const forLevel = highest + 1;
  const ready = r ?? readinessFor(catalog.profile, forLevel, { highestCleared: highest });
  if (ready.state === "ready" || ready.state === "noGearNeeded") return null;
  const level = highest;
  const { gold, extraGearPercent: extra, line: payout } = farmPayout(B, level);
  const live = now !== null && eventsUnlocked(B.events, highest) ? eventAt(B.events, now) : null;
  const event = live ? { id: live.id, name: live.name, endsAt: eventEndsAt(live) } : null;
  const short = ready.shortfall === "towers" ? "your towers are behind" : ready.shortfall === "gear" ? "your gear falls short" : "your gear and towers fall short";
  const fix = ready.shortfall === "towers" ? "for gold to upgrade them" : "for gear, Forge Essence and gold";
  const hint = `Not ready for level ${forLevel}: ${short}. Farm level ${level} ${fix}${event ? ` — every win also earns ${event.name} embers toward its chests` : ""}.`;
  return { level, forLevel, button: `FARM L${level}`, payout, hint, event, gold, extraGearPercent: extra };
}
